import { GRID_SIZE, LAYOUT_M_OPTIONS, LAYOUT_L_OPTIONS, FILTER_OPTIONS } from '@modules/plp/plp.constants';
import { Monogram, Block, Seo } from '@definitions/common.types';
import { PRODUCT_TYPE } from '@utils/constants';

export interface ArtworksState {
  gridSize: GRID_SIZE;
  artworks: Artwork[];
  favoriteArtworks: Artwork[];
  artworkDynamicData: ArtworkDynamicData;
  meta: Meta;
  loading: boolean;
  currentPage: number;
}

export interface ChangeGridSizePayload {
  gridSize: GRID_SIZE;
}

export enum IMAGE_URL_TYPE {
  URL_STANDARD = 'urlStandard',
  URL_THUMBNAIL = 'urlThumbnail',
  URL_TINY = 'urlTiny',
  URL_ZOOM = 'urlZoom',
}

export interface Image {
  imageFile: string;
  isThumbnail: boolean;
  sortOrder: number;
  urlStandard: string;
  urlThumbnail: string;
  urlTiny: string;
  urlZoom: string;
  description: string;
}

export interface Biography {
  paragraph: string;
}

export interface GalleryAddress {
  slug: string;
  name: string;
  addressCity: string;
  addressCountry: string;
  addressStreet: string;
  addressStreet2: string;
  addressZipCode: string;
  internalLink?: string;
}

export interface Artist {
  slug: string;
  firstName: string;
  middleName: string;
  lastName: string;
  biography: Biography;
  artistParagraph: string | Record<string, never>;
}

export interface ArtworkDescription {
  shortDescription: string;
  longDescription: string;
  bulletPoints: string[];
}

export interface VariantOptions {
  id: number;
  label: string;
  optionDisplayName: string;
}

export interface OptionsValues extends VariantOptions {
  isDefault: boolean;
}

export interface ArtworkVariant {
  bigCommerceId: string;
  inventoryLevel: number;
  optionValues: VariantOptions[];
}

export interface ArtworkOptions {
  displayName: string;
  optionValues: OptionsValues[];
}

export interface Artwork {
  variants: ArtworkVariant[];
  options: ArtworkOptions[];
  bcId: number;
  baseVariantId: number;
  images: Image[];
  price: number;
  inventoryLevel: number;
  primaryImage?: Image;
  slug: string;
  title: string;
  wysiwygTitle: string;
  type: PRODUCT_TYPE;
  layoutL: LAYOUT_L_OPTIONS;
  layoutM: LAYOUT_M_OPTIONS;
  artist: Artist;
  year: string;
  medium: string;
  favorite: boolean;
  waitlist: boolean;
  unframedHeight: number;
  unframedWidth: number;
  unframedDepth: number;
  isFramed: boolean;
  isTrade?: boolean;
  framedHeight: number;
  framedWidth: number;
  framedDepth: number;
  isMatted: boolean;
  mattedHeight: number;
  mattedWidth: number;
  mattedDepth: number;
  addToWaitlist: boolean;
  liveStartAt: string;
  liveEndAt: string;
  subcategory: string;
  showSpecs: boolean;
  specsDescription: string;
  urlStandard: string | null;
  gallery: GalleryAddress;
  description: ArtworkDescription;
  additionalArtistBiography: string;
  shows: Block[];
  monogram: Monogram;
  recommendedArtworks: Artwork[];
  seo: Seo | null;
  maximumUnitsPerCart?: number;
  lowInventoryLevel: number;
  artistDesignedPlpLink?: {
    type: string;
    slug: string;
    displayText: string;
  };
  category: string;
}

export interface FetchArtworksPayload {
  artworks: Artwork[];
  meta: Meta;
  infiniteLoader?: boolean;
  filterUrl?: string;
  filter?: FILTER_OPTIONS;
}

export interface FetchFavoriteArtworksPayload {
  artworks: Artwork[];
  meta: Meta;
  infiniteLoader?: boolean;
  filterUrl?: string;
}

export interface SetInitialArtworkPayload {
  artworks: Artwork[];
  meta: Meta;
}

export enum SORT_TYPES {
  PRICE = 'price',
  SIZE = 'size',
  NEWEST = 'newest',
}

export enum DIRECTION_TYPES {
  ASC = 'asc',
  DESC = 'desc',
}

export interface FetchArtworksState {
  page: number;
  infiniteLoader?: boolean;
  filterOptions?: {
    direction?: DIRECTION_TYPES;
    sort?: SORT_TYPES | null;
    filter?: FILTER_OPTIONS;
    [key: string]: any;
  };
  search?: string | string[];
  limit?: number;
  offset?: number;
}

export interface FetchGroupingArtworksState extends FetchArtworksState {
  slug?: string;
}

export interface RequestAddFavoritePayload {
  slug: string;
}

export interface ResponseAddFavoritePayload {
  slug: string;
}

export interface RequestAddWaitlistPayload {
  email: string;
  firstName: string;
  lastName: string;
  slug: string;
}

export interface ResponseAddWaitlistPayload {
  email: string;
  firstName: string;
  lastName: string;
  slug: string;
}

export interface RequestRemoveWaitlistPayload {
  slug: string;
}

export interface ResponseRemoveWaitlistPayload {
  slug: string;
}

export interface Meta {
  count: number;
  hasNext: boolean;
  page: number;
  artworkData: {
    title: string;
    description: string;
  };
}

export interface SlideArtwork {
  artist: {
    firstName: string | null;
    middleName: string | null;
    lastName: string | null;
  };
  title: string;
  year: string;
  price: number;
}

export type ArtworkDynamicData = Partial<Artwork>;
